import React, { useState } from 'react';
import {
  Eye,
  EyeOff,
  User,
  Mail,
  Lock,
  CheckCircle,
  AlertCircle,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { httpClient } from '../axios';
import { useSelector, useDispatch } from 'react-redux';
import { setLogin } from '../store/authSlice';

// Move InputField outside of Register component
const InputField = ({
  label,
  name,
  type = 'text',
  placeholder,
  icon: Icon,
  showPasswordToggle = false,
  showPassword: showPwd,
  onTogglePassword,
  value,
  onChange,
  error,
}) => (
  <div className='space-y-2'>
    <label className='block text-sm font-medium text-slate-200'>
      {label}
    </label>
    <div className='relative'>
      <div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>
        <Icon className='h-5 w-5 text-slate-400' />
      </div>
      <input
        type={
          showPasswordToggle ? (showPwd ? 'text' : 'password') : type
        }
        name={name}
        value={value}
        onChange={onChange}
        className={`block w-full pl-10 pr-10 py-3 bg-slate-800/50 backdrop-blur-sm border-2 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200 ${
          error
            ? 'border-red-500'
            : 'border-slate-600 hover:border-slate-500'
        }`}
        placeholder={placeholder}
      />
      {showPasswordToggle && (
        <button
          type='button'
          onClick={onTogglePassword}
          className='absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-300'
        >
          {showPwd ? (
            <EyeOff className='h-5 w-5' />
          ) : (
            <Eye className='h-5 w-5' />
          )}
        </button>
      )}
    </div>
    {error && (
      <div className='flex items-center gap-2 text-red-400 text-sm'>
        <AlertCircle className='h-4 w-4' />
        {error}
      </div>
    )}
  </div>
);

// Register Component
export const Register = ({ onRegister, onSwitchToLogin }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] =
    useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);

  const calculatePasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 8) strength += 25;
    if (/[A-Z]/.test(password)) strength += 25;
    if (/[0-9]/.test(password)) strength += 25;
    if (/[^A-Za-z0-9]/.test(password)) strength += 25;
    return strength;
  };

  const getPasswordStrengthColor = (strength) => {
    if (strength < 25) return 'bg-red-500';
    if (strength < 50) return 'bg-orange-500';
    if (strength < 75) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const getPasswordStrengthText = (strength) => {
    if (strength < 25) return 'Weak';
    if (strength < 50) return 'Fair';
    if (strength < 75) return 'Good';
    return 'Strong';
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    } else if (formData.fullName.length < 2) {
      newErrors.fullName = 'Full name must be at least 2 characters';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    let obj = {
      fullName: formData.fullName,
      email: formData.email,
      password: formData.password,
    };
    if (validate()) {
      setIsLoading(true);

      // Simulate API call
      try {
        const res = await httpClient.post(`/api/auth/register`, obj);
        console.log(res.data);
        dispatch(setLogin(res.data));
        navigate('/');
      } catch (error) {
        alert(error.response.data.message);
        console.log(error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (name === 'password') {
      setPasswordStrength(calculatePasswordStrength(value));
    }

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  return (
    <div className='min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4'>
      <div className='relative w-full max-w-lg'>
        {/* Background decoration */}
        <div className='absolute -top-4 -left-4 w-72 h-72 bg-purple-600 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse'></div>
        <div className='absolute -bottom-4 -right-4 w-72 h-72 bg-blue-600 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse'></div>

        {/* Main form container */}
        <div className='relative bg-slate-800/30 backdrop-blur-xl border border-slate-700/50 shadow-2xl rounded-3xl p-8 w-full'>
          {/* Header */}
          <div className='text-center mb-8'>
            <div className='inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-purple-600 to-blue-600 rounded-2xl mb-4 shadow-lg'>
              <User className='w-8 h-8 text-white' />
            </div>
            <h2 className='text-3xl font-bold text-white mb-2'>
              Join Us
            </h2>
            <p className='text-slate-400'>
              Create your account to get started
            </p>
          </div>

          <form onSubmit={handleSubmit} className='space-y-6'>
            <InputField
              label='Full Name'
              name='fullName'
              placeholder='Enter your full name'
              icon={User}
              value={formData.fullName}
              onChange={handleChange}
              error={errors.fullName}
            />

            <InputField
              label='Email Address'
              name='email'
              type='email'
              placeholder='Enter your email'
              icon={Mail}
              value={formData.email}
              onChange={handleChange}
              error={errors.email}
            />

            <div className='space-y-2'>
              <InputField
                label='Password'
                name='password'
                placeholder='Create a password'
                icon={Lock}
                showPasswordToggle={true}
                showPassword={showPassword}
                onTogglePassword={() =>
                  setShowPassword(!showPassword)
                }
                value={formData.password}
                onChange={handleChange}
                error={errors.password}
              />

              {/* Password strength indicator */}
              {formData.password && (
                <div className='space-y-2'>
                  <div className='flex items-center justify-between text-sm'>
                    <span className='text-slate-400'>
                      Password strength:
                    </span>
                    <span
                      className={`font-medium ${
                        passwordStrength >= 75
                          ? 'text-green-400'
                          : passwordStrength >= 50
                          ? 'text-yellow-400'
                          : passwordStrength >= 25
                          ? 'text-orange-400'
                          : 'text-red-400'
                      }`}
                    >
                      {getPasswordStrengthText(passwordStrength)}
                    </span>
                  </div>
                  <div className='w-full bg-slate-700 rounded-full h-2'>
                    <div
                      className={`h-2 rounded-full transition-all duration-300 ${getPasswordStrengthColor(
                        passwordStrength
                      )}`}
                      style={{ width: `${passwordStrength}%` }}
                    ></div>
                  </div>
                </div>
              )}
            </div>

            <InputField
              label='Confirm Password'
              name='confirmPassword'
              placeholder='Confirm your password'
              icon={Lock}
              showPasswordToggle={true}
              showPassword={showConfirmPassword}
              onTogglePassword={() =>
                setShowConfirmPassword(!showConfirmPassword)
              }
              value={formData.confirmPassword}
              onChange={handleChange}
              error={errors.confirmPassword}
            />

            <button
              type='submit'
              disabled={isLoading}
              className='w-full py-3 px-6 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl font-semibold shadow-lg hover:from-purple-700 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 transform hover:scale-105'
            >
              {isLoading ? (
                <div className='flex items-center justify-center gap-2'>
                  <div className='w-5 h-5 border-2 border-white/20 border-t-white rounded-full animate-spin'></div>
                  Creating Account...
                </div>
              ) : (
                <div className='flex items-center justify-center gap-2'>
                  <CheckCircle className='w-5 h-5' />
                  Create Account
                </div>
              )}
            </button>

            <div className='relative'>
              <div className='absolute inset-0 flex items-center'>
                <div className='w-full border-t border-slate-600'></div>
              </div>
              <div className='relative flex justify-center text-sm'>
                <span className='px-2 bg-slate-800/30 text-slate-400'>
                  Already have an account?
                </span>
              </div>
            </div>

            <button
              type='button'
              onClick={() => {
                navigate('/login');
              }}
              className='w-full py-3 px-6 bg-transparent border-2 border-slate-600 text-slate-300 rounded-xl font-semibold hover:border-purple-500 hover:text-purple-300 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-slate-800 transition-all duration-200'
            >
              Sign In Instead
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Register;
